import { MinsterTextAreaComponent } from "../ui-components/minster-text-area/minster-text-area.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { ScreenType } from '../../models/enums.models';
import { Component, ViewChild } from '@angular/core';
import { Accident } from '../../models/claim.model';
import { LookUp } from '../../models/look-up.model';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { toolTipMap } from  '../../models/tool-tip-map.model';

@Component({
    selector: 'app-your-accident-description',
    standalone: true,
    imports: [MinsterTextAreaComponent, MinsterSelectComponent, MinsterCardComponent, 
        MatButtonModule, FormsModule, MinsterCheckboxComponent],
    templateUrl: './your-accident-description.component.html',
    styleUrl: './your-accident-description.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class YourAccidentDescriptionComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _accident: Accident;
    _accidentSummary: LookUp[];
    _vehicleSpeed: LookUp[];
    _readonly: boolean = false;
    _currentAccidentSummaryOption: number | null = null; 

    constructor(stateService: StateService, router: Router, toastr: ToastrService, claimService: ClaimsService) {
        super(router, ScreenType.YourAccidentDescription, toastr, stateService, claimService);
        
        this._readonly = stateService.formIsSubmitted;
        this._accident = stateService.claim?.accident!;
        this._accidentSummary = stateService.lookUps!.lookUpDictionary['AccidentSummaryDigitalOnBoarding'];
        this._vehicleSpeed = stateService.lookUps!.lookUpDictionary['VehicleSpeed'];  
    }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }
 
    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        if (this._formGroup && this._formGroup.invalid) {
            let hasErrors = false;

            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];

                        if (this.stateService.formIsSubmitted) {
                            hasErrors = true;
                        }
                        else if (!hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    } 
  

     get _toolTip(): string {
        const accidentValue = this._accident.summaryId;
        if (!accidentValue || accidentValue < 1 || accidentValue > 11) return '';

        const tooltipMap: { [key: number]: string } = {
            1: 'This type of accident typically occurs when a vehicle emerges from a side road, such as at a junction or crossroads, and collides with another vehicle travelling along the main road. Example: I was driving along when a vehicle emerged from a junction on my left and collided with the passenger side of my car.',
            2: 'This refers to an incident where a vehicle was stationary and parked on a public road or private property and was hit by another vehicle (not including inside of a car park). Example: I was seated inside my parked vehicle when another car collided with it.',
            3: 'An accident in a car park refers to an incident that occurs within the boundary of a designated parking area. Example: While driving through a car park, I observed a vehicle ahead beginning to pull out of a car parking space. I stopped to allow the driver to pull out of the car parking space. At that moment, another vehicle positioned to my right began reversing out of a parking space and appeared not to notice my stationary vehicle, hitting my vehicle.',
            4: 'Rear-end collisions are often caused by distracted drivers or tailgating. This is when a vehicles front-end has collided with another vehicle rear-end. Example: I was stationary in a queue of traffic when the vehicle behind me failed to stop and collided with the rear of my car.',
            5: 'Accidents that happen on a roundabout often stem from driver error, including failing to give way, incorrect lane usage and lack of signalling leading to a collision. Example: I was driving around a roundabout when another vehicle entered the roundabout and failed to give way, colliding with the passenger side of my car.',
            6: 'This refers to incidents where a vehicle is moving from one lane to another and causes a collision. Example: I was driving in the middle lane of the motorway when a vehicle in the lane next to me merged into my lane without noticing my car, resulting in a collision.',
            7: 'A concertina collision is a type of road traffic accident involving multiple vehicles, where one initial impact causes a chain reaction of collisions like a domino effect. Example: I was driving slowly in a queue of traffic when the vehicle behind me failed to stop and collided with the rear of my car, pushing my vehicle forward into the vehicle in front.',
            8: 'Refers to situations where a driver gives incorrect or confusing signals, such as using indicators or brake lights in a way that misleads other road users and causes an accident as a result. Example: I was driving down a main road when another vehicle indicated left, appearing to pull over to the side of the road. As I began to pass on the right, the other vehicle unexpectedly moved back into the lane, resulting in a collision between our cars.',
            9: 'This type of accident occurs when a vehicle attempts to overtake another, typically on the right, in an unsafe manner, such as without sufficient space, visibility, or time to complete the manoeuvre safely. Example: I was driving along a single carriageway when another vehicle attempted to overtake me on the right. The driver misjudged the space available and clipped the side of my car while trying to pass, resulting in a collision.',
            10: 'A head-on collision is a type of road traffic accident where the front ends of two vehicles travelling in opposite directions crash into each other. Example: I was driving uphill when a vehicle coming downhill veered onto my side of the road. I was unable to stop or avoid the vehicle, and it collided with the front of my car.',
            11: 'This category covers any accident circumstances that do not fall under the specific types listed above, such as car park incidents or parked vehicle collisions. If your situation is different from those examples, please provide a brief description. Example: I had the rear door of my vehicle open while unloading shopping from the back seat. During this time, another vehicle collided with my car, causing the open door to be pushed into me.'
        };

        return tooltipMap[accidentValue] || '';
    }

  
    get isSelectedOption(): boolean {
        return !!(this._accident.summaryId && this._accident.summaryId >= 1 && this._accident.summaryId <= 11);
    }
    
}
